/**
 * PollOptionRow - Single option in a poll detail view.
 * Design: tripways.pen node pALSF (open, node nqsWZ/d7hkw) / H2UpKB (closed, node cQghL/mPwyF)
 *
 * Open: clickable to vote/revoke, myVoted option gets a primary border + "已選" badge.
 * Closed: read-only, the highest-voted option(s) get a trophy "最高票" badge instead.
 */

import { Check, Trophy } from 'lucide-react';
import type { PollOption, PollStatus } from '../../types/poll';

interface PollOptionRowProps {
  option: PollOption;
  totalVotes: number;
  status: PollStatus;
  isWinner?: boolean;
  /** true when this option should not be clickable even though the poll is open (e.g. multi-select cap reached and not yet selected) */
  disabled?: boolean;
  onToggle?: () => void;
}

export default function PollOptionRow({
  option,
  totalVotes,
  status,
  isWinner = false,
  disabled = false,
  onToggle,
}: PollOptionRowProps) {
  const isOpen = status === 'OPEN';
  const percentage = totalVotes > 0 ? Math.round((option.voteCount / totalVotes) * 1000) / 10 : 0;
  const highlighted = isOpen ? option.myVoted : isWinner;

  const content = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[15px] font-medium text-slate-900">{option.label}</span>
        <div className="flex items-center gap-2 shrink-0">
          {isOpen && option.myVoted && (
            <span className="flex items-center gap-1 text-[11px] font-semibold text-primary bg-[#e0f9fe] rounded-lg px-2 py-[3px]">
              <Check size={12} />
              已選
            </span>
          )}
          {!isOpen && isWinner && (
            <span className="flex items-center gap-1 text-[11px] font-semibold text-primary bg-[#e0f9fe] rounded-lg px-2 py-[3px]">
              <Trophy size={12} />
              最高票
            </span>
          )}
          <span className="text-[13px] text-slate-500">
            {option.voteCount} 票 · {percentage}%
          </span>
        </div>
      </div>
      <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${highlighted ? 'bg-primary' : 'bg-slate-300'}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </>
  );

  const borderClass = highlighted ? 'border-primary' : 'border-transparent';

  if (!isOpen || !onToggle) {
    return (
      <div className={`rounded-xl bg-white shadow-sm border-2 ${borderClass} p-4 lg:p-5 flex flex-col gap-2.5`}>
        {content}
      </div>
    );
  }

  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`w-full text-left rounded-xl bg-white shadow-sm border-2 ${borderClass} p-4 lg:p-5 flex flex-col gap-2.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/40`}
    >
      {content}
    </button>
  );
}
